/* eslint-disable no-underscore-dangle */
import React, { useCallback } from 'react';
import {
  FieldProps,
  ErrorMessage,
} from 'formik';
import {
  components,
} from 'react-select';

import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  Select,
} from 'web-components';
import {
  uiActions,
} from 'web-actions';
import {
  Flex,
  Text,
  SVG,
} from 'web-styled';

import * as S from './styled';

const mapState = state => ({
  options: coreSelectors.getTagOptions(state),
});

export type Props = {
  flex: string;
  width?: string;
  spacing?: number;
  label?: string;
  color?: string;
  placeholder?: string;
  onChange: (data: any) => void;
} & FieldProps;

const CustomOption = ({ label, value, innerRef, innerProps, selectProps, data, ...restProps }: any) => {
  const dispatch = useDispatch();

  const handleRemove = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!value) return
    const onConfirmAction = coreActions.requestRemoveTag({
      id: value,
    });

    dispatch(
      uiActions.confirmModal({
        modal: {
          title: 'Delete tag?',
          bodyText: `Are you sure you want to delete the tag <b>${label}</b>?`,
          confirmText: 'Delete',
        },
        onConfirmAction,
      }),
    );
  }, [dispatch, value]);

  return (
    <components.Option {...selectProps} {...restProps}>
      <S.TagOption {...innerProps} ref={innerRef}>
        <Text>
          {label}
        </Text>
        {restProps.isFocused && !data.__isNew__ && (
          <div className="remove-tag" onClick={handleRemove}>
            <SVG.X size={14} strokeWidth={3} />
          </div >
        )}
      </S.TagOption >
    </components.Option >
  )
}

const TagField = ({
  label = 'Tags',
  field,
  form: { touched, errors, setFieldValue },
  placeholder = 'Type here to create or select tags...',
  flex,
  width = '100%',
  spacing = 10,
  color = 'primary',
  onChange,
}: Props) => {
  const iserror = (touched[field.name] && errors[field.name]);
  const { options } = useSelector(mapState, shallowEqual);
  const dispatch = useDispatch();

  return (
    <Flex
      column
      width={width}
      spacing={spacing}
      flex={flex}
    >
      <S.Label
        htmlFor={`${field.name}-id`}
        color={color}
      >
        {label}
      </S.Label>

      <Select
        value=""
        creatable
        options={options}
        placeholder={placeholder}
        createOptionPosition="first"
        formatCreateLabel={userInput => (
          <Flex align="center" spacing={10}>
            <S.PlusCircle />
            <span>
              {`Create tag "${userInput}"`}
            </span>
            <S.Keystroke>
              Enter
            </S.Keystroke>
          </Flex>
        )}
        theme={{
          borderRadius: '0.25rem',
        }}
        styles={{
          container: provided => ({
            ...provided,
            width: '100%',
          }),
          control: provided => ({
            ...provided,
            height: '40px',
            minHeight: '34px',
            boxShadow: 'none',
            borderColor: iserror ? '#de350b' : '#ddd',
            borderWidth: iserror ? '2px' : '1px',
            '&:focus': {
              borderColor: iserror ? '#de350b' : '#ddd',
            },
            '&:hover': {
              borderColor: iserror ? '#de350b' : '#ddd',
            },
          }),
        }}
        components={{
          Option: CustomOption,
        }}
        onChange={(option: any) => {
          if (option?.label) {
            const newTags = field.value
              ? [
                ...new Set([
                  ...field.value.split(','),
                  option.label,
                ]),
              ].join(',')
              : option.label;

            if (option.__isNew__) {
              dispatch(
                coreActions.requestCreateTag({
                  name: option.label,
                }),
              );
            }

            setFieldValue(
              field.name,
              newTags,
            );
          }

          if (onChange) onChange(option);
        }}
      />

      <ErrorMessage
        name={field.name}
        component={S.Error}
      />

      {field.value && (
        <Flex
          wrap="wrap"
          align="center"
          spacing={6}
        >
          {field.value.split(',').sort().map(tag => (
            <S.Tag key={tag}>
              <span>
                {tag}
              </span>
              <Flex
                clickable
                onClick={() => {
                  setFieldValue(
                    field.name,
                    field.value
                      .split(',')
                      .filter(t => t !== tag)
                      .join(','),
                  );
                }}
              >
                <SVG.X size={14} strokeWidth={3} />
              </Flex>
            </S.Tag>
          ))}
        </Flex>
      )}
    </Flex>
  );
};

export default TagField;
